import { useState, useEffect, useMemo, useRef } from "react";
/*
 * All from `react-router-dom`, not some from bare `react-router`.
 *
 * The dom package re-exports the other, so in a browser build both resolve to one
 * module and share the router context. Under Node they do not: `react-router-dom`
 * resolves to its CJS build and a bare `react-router` to the ESM one, which is two
 * copies of the context and a component that cannot see the Router it is inside.
 */
import { Link, useMatch, useNavigate } from "react-router-dom";
import classNames from "classnames";
import * as tools from "@app/utils/tools";
import { filterItems } from "@app/utils/filter";
import { Item } from "@app/components/Item";
import { ItemForm, ItemFormValues } from "@app/components/ItemForm";
import { ResultsPane } from "@app/components/ResultsPane";
import { SegmentedControl } from "@app/components/SegmentedControl";
import { ToggleButton } from "@app/components/ToggleButton";
import { FilterField } from "@app/components/FilterField";
import { Select } from "@app/components/Select";
import { Button } from "@app/components/Button";
import { PlusIcon, SignInIcon } from "@app/components/icons";
import { Sorting, usePreferences } from "../contexts/PreferencesContext";
import { Provider } from "@app/utils/searchProviders";
import { AppItem } from "@app/types";
import { useMountContext } from "./Mount";
import { Modal } from "./Modal";
import { useAuth } from "@app/contexts/AuthContext";
import { useApiDispatch } from "@app/contexts/ApiDispatch";
import { useApiCall } from "@app/hooks/useApiCall";
import {
  apiDeleteItemsId,
  apiGetItems,
  apiPostItems,
  apiPutItemsId,
} from "@app/backendApi/actions";

/** Matches the `md` breakpoint the layout switches at. */
const WIDE = "(min-width: 48rem)";


export function App() {
  const auth = useAuth();
  const [items, setItems] = useState<AppItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [filterText, setFilterText] = useState("");
  const {
    sorting,
    sortings,
    sortingFunction: sortFunction,
    setSorting,
    providers,
    provider: searchProvider,
    setProvider,
    itemLinkMode,
    setItemLinkMode,
  } = usePreferences();
  const itemsIsHiresMode = itemLinkMode === "hires";
  const filterInputRef = useRef<HTMLInputElement>(null);
  const itemsListRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const mount = useMountContext();
  const id = useMatch("/edit/:id")?.params.id;
  const apiDispatch = useApiDispatch();
  const apiCall = useApiCall();

  const currentItem = id ? items.find((i) => i.id === id) : undefined;

  /*
   * Focusing the filter on load is a desktop convenience and a mobile annoyance: on a
   * phone it throws up the keyboard over the list before anything has been read.
   * Read once, since the layout it belongs to does not change under a rotation that
   * matters here. Optional-called because jsdom has no matchMedia.
   */
  const [wide] = useState(() => window.matchMedia?.(WIDE).matches ?? false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        e.preventDefault();
        filterInputRef.current?.focus();
        filterInputRef.current?.select();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const getItems = () => {
    return apiDispatch(apiGetItems())
      .then((data) => {
        setItems(data);
      })
      .catch(console.error)
      .finally(() => setLoaded(true));
  };

  const sortedItems = useMemo(() => {
    const sorted = items.toSorted(sortFunction);
    sortFunction.reset?.();
    return sorted;
  }, [items, sortFunction]);

  const filteredItems = useMemo(
    () => filterItems(sortedItems, filterText),
    [sortedItems, filterText]
  );

  const tags = useMemo(() => {
    return items
      .reduce<string[]>((c, x) => {
        for (const tag of x.tags) {
          if (c.indexOf(tag) === -1) c.push(tag);
        }
        return c;
      }, [])
      .sort();
  }, [items]);

  const handleOpenAddNewItem = () => {
    const handleAddNewItem = async (data: ItemFormValues) => {
      try {
        const item = await apiCall(apiPostItems(data), "add this item");
        await getItems();
        setFilterText("");
        scrollToItem(item);
        navigate(`/edit/${item.id}`);
        mount.close(id);
      } catch (e) {
        console.error(e);
      }
    };

    const id = mount.push({
      element: (
        <Modal title="Add new item">
          <ItemForm
            onSave={handleAddNewItem}
            tagsAutocomplete={tags}
            checkDuplicates
          />
        </Modal>
      ),
    });
  };

  /*
   * The same form as adding, with a Delete — which is the whole difference between the
   * two, and the reason this is one component rather than two.
   *
   * `item` is passed in rather than read from `currentItem` inside the modal: the modal
   * is rendered once onto the mount stack and does not re-render with this component,
   * so a captured value is the honest thing to hold.
   */
  const handleOpenEditItem = (item: AppItem) => {
    const handleSave = async (data: ItemFormValues) => {
      try {
        await apiCall(apiPutItemsId(item.id, data), "save this item");
        await getItems();
        mount.close(id);
      } catch (e) {
        console.error(e);
      }
    };

    const handleDeleteFromModal = () => {
      if (!confirm(`Delete "${item.name}"?`)) return;
      mount.close(id);
      handleDelete(item.id);
    };

    const id = mount.push({
      element: (
        <Modal title="Edit item">
          <ItemForm
            tagsAutocomplete={tags}
            initial={{ name: item.name, tags: item.tags, rating: item.rating }}
            submitLabel="Save"
            onSave={handleSave}
            onDelete={handleDeleteFromModal}
          />
        </Modal>
      ),
    });
  };

  const handleLocationChange = (id: string) => {
    navigate(`/edit/${id}`);
  };

  const scrollToItem = (item: AppItem) => {
    if (!itemsListRef.current) return;
    const el = itemsListRef.current.querySelector(
      `[data-item-id="${item.id}"]`
    );
    if (!el) return;
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });
  };

  const handleItemChange = (newItem: AppItem) => {
    apiCall(
      apiPutItemsId(newItem.id, {
        name: newItem.name,
        tags: newItem.tags,
        rating: newItem.rating,
      }),
      "save this item"
    )
      .then(() => getItems())
      // Already reported by apiCall; this stops the rejection going unhandled.
      .catch(() => {});
  };

  const handleDelete = (id: string) => {
    apiCall(apiDeleteItemsId(id), "delete this item")
      .then(() => getItems())
      .then(() => navigate("/", { replace: true }))
      .catch(() => {});
  };

  const handleSortSelect = ({ label }: { label: string }) => {
    setSorting(label.toLocaleLowerCase() as Sorting);
  };

  const handleTagSelected = ({ tag }: { tag: string }) => {
    setFilterText(tag);
  };

  useEffect(() => {
    getItems();
  }, []);

  useEffect(() => {
    if (id && !currentItem && loaded) navigate(`/`);
  });

  if (!loaded) {
    return (
      <div className="flex h-dvh items-center justify-center text-sm text-ink-muted">
        Loading…
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden md:flex-row">
      {/*
        The list. Full width on a phone, where it is the whole screen until something
        is picked; a fixed column from the `md` breakpoint up.
      */}
      <aside className="flex min-h-0 w-full flex-col bg-surface-sunken md:w-80 md:shrink-0 md:border-r md:border-line lg:w-88">
        <header className="flex items-center gap-2 px-3 py-2.5">
          <h1 className="flex-1 text-sm font-semibold tracking-tight text-ink">
            ArtNames
          </h1>
          {auth.state === "authenticated" ? (
            <Button variant="primary" onClick={handleOpenAddNewItem}>
              <PlusIcon />
              Add
            </Button>
          ) : (
            <Link
              to="/login"
              className="inline-flex h-8 cursor-pointer items-center justify-center gap-1.5
                rounded-md border border-line-strong bg-surface-raised px-3 text-sm
                font-medium text-ink no-underline transition-colors hover:bg-surface-sunken
                focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <SignInIcon />
              Log in
            </Link>
          )}
        </header>

        <div className="flex flex-col gap-2 px-3 pb-2.5">
          <FilterField
            value={filterText}
            onChange={setFilterText}
            inputRef={filterInputRef}
            autoFocus={wide}
          />
          <SegmentedControl
            label="Sort by"
            items={sortings.map((x) => tools.toTitleCase(x))}
            selectedItem={sortings.findIndex((x) => x === sorting)}
            onSelect={handleSortSelect}
          />
        </div>

        <div
          className="scroll-slim min-h-0 flex-1 overflow-y-auto border-t border-line px-1.5 py-2"
          ref={itemsListRef}
        >
          {filteredItems.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-ink-muted">
              {items.length === 0
                ? "Nothing here yet."
                : `No names match “${filterText}”.`}
            </p>
          ) : (
            filteredItems.map((item) => (
              <div key={item.id} data-item-id={item.id}>
                <Item
                  current={item.id === id}
                  item={item}
                  onLocationChange={handleLocationChange}
                  onChange={handleItemChange}
                  onTagSelected={handleTagSelected}
                />
              </div>
            ))
          )}
        </div>

        <footer className="flex items-center gap-2 border-t border-line px-3 py-2">
          <span className="text-micro text-ink-muted">
            {filteredItems.length === items.length
              ? `${items.length} names`
              : `${filteredItems.length} of ${items.length}`}
          </span>
          <span className="flex-1" />
          <Select
            aria-label="Search provider"
            value={searchProvider}
            onChange={(e) => setProvider(e.target.value as Provider)}
          >
            {providers.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </Select>
          <ToggleButton
            label="hires"
            value={itemsIsHiresMode}
            onInput={(v) => setItemLinkMode(v ? "hires" : "standard")}
          />
        </footer>
      </aside>

      {/*
        The results pane.

        On a phone it is a modal over the list rather than a second column: at 375px a
        two-pane split leaves both halves too narrow to be worth reading, which is what
        the old fixed-width sidebar did. From `md` up it is an ordinary flex child and
        the `fixed` positioning is dropped.
      */}
      <main
        className={classNames(
          "min-w-0 bg-surface",
          currentItem
            ? "fixed inset-0 z-40 flex flex-col md:static md:z-auto md:flex-1"
            : "hidden md:flex md:flex-1 md:flex-col"
        )}
      >
        {currentItem ? (
          <ResultsPane
            key={currentItem.id}
            item={currentItem}
            onClose={() => navigate("/")}
            onEdit={
              auth.state === "authenticated" ? handleOpenEditItem : undefined
            }
          />
        ) : (
          <div className="flex flex-1 items-center justify-center p-8 text-center text-sm text-ink-muted">
            Pick a name to see what it looks like.
          </div>
        )}
      </main>
    </div>
  );
}
